import { catchErrorWithStack, throwErrorWithStack } from "./crashlytics";
import { getLogger } from "./logging";
import { FirebaseFirestore } from "./plugins";
import { withInternetConnected, withTimeout } from "./promise";

const log = getLogger("🗂️ Firestore");

class FirestoreWrapper {
  constructor() {
    this.cache = {};
    this.collectionCache = {};
  }

  getDocument = async ({
    reference,
    useCache = true,
    defaultData,
    showSnackbarOnError = false,
    timeoutArgs = [],
  }) => {
    if (useCache && this.cache[reference]) {
      log.debug(`Using cache for ${reference}`);
      return this.cache[reference];
    }
    log.debug(`Getting document ${reference}`);
    try {
      const response = await withTimeout(
        withInternetConnected(FirebaseFirestore.getDocument({ reference })),
        ...timeoutArgs
      );
      const data = response.snapshot.data;
      this.cache[reference] = data;
      return data;
    } catch (error) {
      if (defaultData === undefined)
        throwErrorWithStack(error, showSnackbarOnError);
      if (error.type != "NO_INTERNET") catchErrorWithStack(error);
      return defaultData;
    }
  };

  setDocument = async ({
    reference,
    data,
    merge = true,
    throwOnError = false,
    timeoutArgs = [],
  }) => {
    log.debug(`Setting document ${reference}`);
    try {
      await withTimeout(
        withInternetConnected(
          FirebaseFirestore.setDocument({
            reference,
            data,
            merge,
          })
        ),
        ...timeoutArgs
      );
      this.cache[reference] = { ...(this.cache[reference] || {}), ...data };
    } catch (error) {
      log.error(data);
      if (throwOnError) throwErrorWithStack(error, true);
      if (error.type == "NO_INTERNET") return;
      catchErrorWithStack(error);
    }
  };

  addDocument = async ({ reference, data, throwOnError = false }) => {
    log.debug(`Adding document ${reference}`);
    try {
      const response = await withTimeout(
        withInternetConnected(
          FirebaseFirestore.addDocument({ reference, data })
        )
      );
      this.cache[reference] = data;
      return response.reference;
    } catch (error) {
      log.error(data);
      if (throwOnError) throwErrorWithStack(error, true);
      if (error.type == "NO_INTERNET") return;
      catchErrorWithStack(error);
    }
  };

  updateDocument = async ({ reference, data, throwOnError = false }) => {
    log.debug(`Updating document ${reference}`);
    try {
      await withTimeout(
        withInternetConnected(
          FirebaseFirestore.updateDocument({ reference, data })
        )
      );
      this.cache[reference] = { ...(this.cache[reference] || {}), ...data };
    } catch (error) {
      log.error(data);
      if (throwOnError) throwErrorWithStack(error, true);
      if (error.type == "NO_INTERNET") return;
      catchErrorWithStack(error);
    }
  };

  deleteDocument = async ({ reference, throwOnError = false }) => {
    log.debug(`Deleting document ${reference}`);
    try {
      await withTimeout(
        withInternetConnected(FirebaseFirestore.deleteDocument({ reference }))
      );
      if (this.cache[reference]) delete this.cache[reference];
    } catch (error) {
      if (throwOnError) throwErrorWithStack(error, true);
      if (error.type == "NO_INTERNET") return;
      catchErrorWithStack(error);
    }
  };

  getCollection = async ({
    reference,
    useCache = true,
    defaultData,
    showSnackbarOnError = false,
    ...options
  }) => {
    const optionsCacheKey = JSON.stringify(options);

    if (this.collectionCache[reference] === undefined)
      this.collectionCache[reference] = {};

    if (useCache && this.collectionCache[reference][optionsCacheKey]) {
      log.debug(`Using cache for collection ${reference}`, options);
      return this.collectionCache[reference][optionsCacheKey];
    }

    log.debug(`Getting collection ${reference}`, options);
    try {
      const { snapshots } = await withTimeout(
        withInternetConnected(
          FirebaseFirestore.getCollection({
            reference,
            ...options,
          })
        )
      );
      this.collectionCache[reference][optionsCacheKey] = snapshots;
      return snapshots;
    } catch (error) {
      if (defaultData === undefined)
        throwErrorWithStack(error, showSnackbarOnError);
      if (error.type != "NO_INTERNET") catchErrorWithStack(error);
      return defaultData;
    }
  };

  clearCache(reference) {
    log.debug(`Clearing cache for ${reference}`);
    if (reference) {
      delete this.cache[reference];
    } else {
      this.cache = {};
    }
  }

  clearCacheForDocumentsInCollection(reference) {
    log.debug(`Clearing cache for documents in collection ${reference}`);
    Object.keys(this.cache).forEach((key) => {
      if (key.startsWith(reference)) {
        delete this.cache[key];
      }
    });
  }

  clearCollectionCache(reference) {
    log.debug(`Clearing collection cache for ${reference}`);
    if (reference) {
      delete this.collectionCache[reference];
    } else {
      this.collectionCache = {};
    }
  }
}
const cachedFirestore = new FirestoreWrapper();

if (__DEV__) window.cachedFirestore = cachedFirestore;

export default cachedFirestore;
